import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutDashboard, Settings, LogOut, Command, ChevronDown, Menu, X, LifeBuoy, Activity, Tags } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useHelp } from "@/context/HelpContext";
import { hasRole } from "@/lib/permissions";
import { api, mediaUrl } from "@/lib/api";
import { iconFor } from "@/lib/menuIcons";
import { GlobalNotes } from "@/components/GlobalNotes";
import { NotificationBell } from "@/components/NotificationBell";
import { MyNotesLink } from "@/components/MyNotesLink";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { openHelp, openCommand } = useHelp();
  const theme = useTheme();
  const mode = theme?.mode || "light";
  const logoUrl = (mode === "light"
    ? (theme?.config?.logo_url_light || theme?.config?.logo_url)
    : theme?.config?.logo_url) || "";
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(localStorage.getItem("mh_site") || "");
  const [menu, setMenu] = useState([]);
  const [open, setOpen] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const openKey = user ? `mh_menu_open_${user.user_id}` : "mh_menu_open";

  useEffect(() => {
    const loadSites = () => api.get("/sites").then((res) => {
      let list = res.data;
      if (user && user.sites !== "all" && Array.isArray(user.sites)) {
        list = list.filter((s) => user.sites.includes(s.id));
      }
      setSites(list);
      if (list.length && !list.find((s) => s.id === activeSite)) {
        setActiveSite(list[0].id);
        localStorage.setItem("mh_site", list[0].id);
      }
    });
    loadSites();
    // Refresh site data (e.g. the dot colour) after a theme save.
    window.addEventListener("mh:sites-updated", loadSites);
    return () => window.removeEventListener("mh:sites-updated", loadSites);
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    api.get("/menu").then((res) => {
      const groups = res.data.groups || [];
      setMenu(groups);
      let saved = {};
      try { saved = JSON.parse(localStorage.getItem(openKey) || "{}"); } catch { saved = {}; }
      // default: every group expanded unless the user previously collapsed it
      const next = {};
      groups.forEach((g) => { next[g.id] = g.id in saved ? saved[g.id] : true; });
      setOpen(next);
    });
  }, [user]); // eslint-disable-line

  const toggleGroup = (id) => {
    setOpen((o) => {
      const next = { ...o, [id]: !o[id] };
      localStorage.setItem(openKey, JSON.stringify(next));
      return next;
    });
  };

  const onSiteChange = (v) => { setActiveSite(v); localStorage.setItem("mh_site", v); };
  const current = sites.find((s) => s.id === activeSite);

  // Tell the ThemeProvider which site's theme to render.
  useEffect(() => {
    if (activeSite) window.dispatchEvent(new CustomEvent("mh:site", { detail: activeSite }));
  }, [activeSite]);

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-950 text-zinc-100 font-[Inter]">
      {/* mobile overlay */}
      {sidebarOpen && (
        <div data-testid="sidebar-overlay" onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm md:hidden" />
      )}
      <aside data-testid="sidebar"
        onClick={(e) => { if (e.target.closest("a")) setSidebarOpen(false); }}
        className={`fixed md:static inset-y-0 left-0 z-[60] w-64 flex-shrink-0 border-r border-zinc-800 bg-zinc-950 flex flex-col transform transition-transform duration-200 md:translate-x-0 md:z-auto ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800">
          {logoUrl ? (
            <img src={mediaUrl(logoUrl)} alt="Dowdens Group" data-testid="sidebar-logo" style={{ width: "50px", height: "auto" }} className="object-contain"
              onError={(e) => { const fb = `${process.env.REACT_APP_BACKEND_URL}/api/assetlibrary/public/signin-logo.png`; if (e.currentTarget.src !== fb) e.currentTarget.src = fb; }} />
          ) : (
            <div className="h-7 w-7 rounded-md bg-indigo-600 flex items-center justify-center font-black text-white text-sm">D</div>
          )}
          <span className="font-[Outfit] font-black tracking-normal text-lg">Marketing Hub</span>
          <button data-testid="sidebar-close" onClick={() => setSidebarOpen(false)}
            className="ml-auto md:hidden text-zinc-500 hover:text-zinc-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 border-b border-zinc-800">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Active Site</p>
          {sites.length === 0 ? (
            <p className="text-xs text-zinc-600 px-1 py-2">No sites assigned yet.</p>
          ) : (
            <Select value={activeSite} onValueChange={onSiteChange}>
              <SelectTrigger data-testid="site-switcher" className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11">
                <div className="flex items-center gap-2 truncate">
                  <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: current?.color || "#4f46e5" }} />
                  <SelectValue placeholder="Select site" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`site-option-${s.domain}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          <NavLink to="/" end data-testid="nav-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-indigo-600/15 text-indigo-300 border border-indigo-600/30" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-transparent"}`}>
            <LayoutDashboard className="h-4 w-4" />
            <span className="flex-1">Dashboard</span>
          </NavLink>

          <NavLink to="/activity" data-testid="nav-activity"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-indigo-600/15 text-indigo-300 border border-indigo-600/30" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-transparent"}`}>
            <Activity className="h-4 w-4" />
            <span className="flex-1">Activity</span>
          </NavLink>

          <NavLink to="/browse" data-testid="nav-browse"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-indigo-600/15 text-indigo-300 border border-indigo-600/30" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-transparent"}`}>
            <Tags className="h-4 w-4" />
            <span className="flex-1">Browse by Tag</span>
          </NavLink>

          <div className="pt-2 space-y-1">
            {menu.map((group) => (
              <div key={group.id} data-testid={`nav-group-${group.id}`}>
                <button
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`nav-group-toggle-${group.id}`}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-bold tracking-[0.16em] uppercase text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open[group.id] ? "" : "-rotate-90"}`} />
                  <span className="flex-1 text-left">{group.label}</span>
                </button>
                <AnimatePresence initial={false}>
                  {open[group.id] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }} className="overflow-hidden pl-2"
                    >
                      {group.items.map((item) => {
                        const Icon = iconFor(item.icon);
                        return (
                          <NavLink key={item.key} to={item.route} data-testid={`nav-${item.key}`}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-indigo-600/15 text-indigo-300 border border-indigo-600/30" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-transparent"}`}>
                            <Icon className="h-4 w-4" />
                            <span className="flex-1">{item.label}</span>
                            {!item.live && <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-600">soon</span>}
                          </NavLink>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {hasRole(user, "manager") && (
            <>
              <div className="pt-3 mt-2 border-t border-zinc-800" />
              <button data-testid="nav-settings" onClick={() => navigate("/settings")}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-transparent transition-colors">
                <Settings className="h-4 w-4" />
                <span className="flex-1 text-left">Settings</span>
              </button>
            </>
          )}
        </nav>

        <div className="p-3 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-semibold text-zinc-200 overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" /> : (user?.name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.role}</p>
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
              className="text-zinc-500 hover:text-red-400 transition-colors" title="Log out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col h-screen overflow-y-auto bg-zinc-950">
        <header className="h-16 flex-shrink-0 flex items-center justify-between px-4 sm:px-6 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl z-50 sticky top-0">
          <div className="flex items-center gap-3 text-sm text-zinc-500 min-w-0">
            <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(true)}
              className="md:hidden text-zinc-400 hover:text-zinc-100 flex-shrink-0"><Menu className="h-5 w-5" /></button>
            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: current?.color || "#4f46e5" }} />
            {current?.domain ? (
              <a data-testid="site-url-link" href={`https://${current.domain}`} target="_blank" rel="noopener noreferrer"
                className="text-zinc-300 font-medium truncate hover:text-indigo-300 hover:underline transition-colors">{current.domain}</a>
            ) : (
              <span className="text-zinc-300 font-medium truncate">Dowdens Group</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button data-testid="need-help-btn" onClick={openHelp}
              className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-indigo-300 border border-zinc-800 hover:border-zinc-700 rounded-lg px-3 py-1.5 transition-colors">
              <LifeBuoy className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Need help?</span>
            </button>
            <GlobalNotes />
            <MyNotesLink />
            <NotificationBell />
            <button data-testid="command-search" onClick={openCommand}
              className="flex items-center gap-2 text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-1.5 hover:border-zinc-700 transition-colors">
              <Command className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Search</span>
              <kbd className="text-[10px] bg-zinc-800 rounded px-1.5 py-0.5">⌘K</kbd>
            </button>
          </div>
        </header>
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
}
